/**
 * UsageDetailsCard Component
 * Expandable card showing detailed usage breakdown
 *
 * Features:
 * - Per-organization SKU usage (if multi-org)
 * - SmartImport history
 * - Default collapsed, auto-expands if usage >90%
 */

import { Card, Collapse, Typography, Space } from 'antd'
import type { UsageMetrics } from '@/types/billing.types'

const { Text } = Typography

interface Props {
  usage?: UsageMetrics
}

export function UsageDetailsCard({ usage }: Props) {
  if (!usage) {
    return null
  }

  // Auto-expand if usage is critical (>90%)
  const defaultExpanded = usage.skuCount.percentage > 90 ? ['usage-details'] : []

  return (
    <Card>
      <Collapse
        ghost
        defaultActiveKey={defaultExpanded}
        items={[
          {
            key: 'usage-details',
            label: 'Detailed Usage',
            children: (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* SKU usage details */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    SKU Usage
                  </Text>
                  <div style={{ background: '#fafafa', padding: '12px', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Current SKU count:</Text>
                      <Text strong>{usage.skuCount.current.toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                      <Text>Plan limit:</Text>
                      <Text strong>{usage.skuCount.limit.toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                      <Text>Percentage used:</Text>
                      <Text strong>{Math.round(usage.skuCount.percentage)}%</Text>
                    </div>
                    {usage.skuCount.inGracePeriod && usage.skuCount.graceUntil && usage.skuCount.targetValue && (
                      <div style={{ marginTop: '12px', padding: '8px', background: '#fff7e6', borderRadius: '4px' }}>
                        <Text type="warning" strong>Grace Period Active</Text>
                        <div style={{ marginTop: '4px' }}>
                          <Text type="secondary">
                            Reduce to {usage.skuCount.targetValue.toLocaleString()} SKUs by{' '}
                            {new Date(usage.skuCount.graceUntil * 1000).toLocaleDateString()}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* SmartImport usage details */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    SmartImport Usage
                  </Text>
                  <div style={{ background: '#fafafa', padding: '12px', borderRadius: '4px' }}>
                    {usage.smartimportRuns.unlimited ? (
                      <div>
                        <Text>Unlimited SmartImport runs</Text>
                        <div style={{ marginTop: '8px' }}>
                          <Text type="secondary">
                            This month: {usage.smartimportRuns.current.toLocaleString()} runs
                          </Text>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text>Runs this month:</Text>
                          <Text strong>{usage.smartimportRuns.current.toLocaleString()}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                          <Text>Monthly limit:</Text>
                          <Text strong>{usage.smartimportRuns.limit?.toLocaleString() || 'N/A'}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                          <Text>Percentage used:</Text>
                          <Text strong>{Math.round(usage.smartimportRuns.percentage)}%</Text>
                        </div>
                      </>
                    )}
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">
                        Resets on {new Date(usage.smartimportRuns.resetsOn).toLocaleDateString()}
                      </Text>
                    </div>
                  </div>
                </div>
              </Space>
            )
          }
        ]}
      />
    </Card>
  )
}
